'use client';

import * as React from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';
import { useBackgroundTheme } from './use-background-theme';

/**
 * CrystalShards — Schwebende rotierende Kristall-Fragmente
 *
 * Technik: CSS 3D transforms + clip-path + framer-motion
 * CPU-Impact: Very Low (GPU-composited, keine Canvas-Berechnung)
 *
 * Verwendung:
 *   <CrystalShardsBackground>
 *     <div className="relative z-10">Content</div>
 *   </CrystalShardsBackground>
 */

// Polygon clip-paths für verschiedene Kristall-Formen
const SHAPES = [
  'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',              // Pentagon
  'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',                         // Raute
  'polygon(50% 0%, 100% 100%, 0% 100%)',                                  // Dreieck
  'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',       // Hexagon
  'polygon(50% 0%, 80% 10%, 100% 35%, 100% 70%, 80% 90%, 50% 100%, 20% 90%, 0% 70%, 0% 35%, 20% 10%)', // Dekagon
  'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',   // Stern
];

const GRADIENTS = [
  'linear-gradient(135deg, rgba(6,182,212,0.6) 0%, rgba(139,92,246,0.3) 50%, rgba(6,182,212,0.1) 100%)',
  'linear-gradient(135deg, rgba(139,92,246,0.6) 0%, rgba(236,72,153,0.3) 50%, rgba(139,92,246,0.1) 100%)',
  'linear-gradient(135deg, rgba(16,185,129,0.6) 0%, rgba(6,182,212,0.3) 50%, rgba(16,185,129,0.1) 100%)',
  'linear-gradient(135deg, rgba(245,158,11,0.5) 0%, rgba(239,68,68,0.3) 50%, rgba(245,158,11,0.1) 100%)',
  'linear-gradient(135deg, rgba(59,130,246,0.6) 0%, rgba(16,185,129,0.3) 50%, rgba(59,130,246,0.1) 100%)',
  'linear-gradient(135deg, rgba(236,72,153,0.5) 0%, rgba(245,158,11,0.3) 50%, rgba(236,72,153,0.1) 100%)',
];

function CrystalShardsBackground({
  className,
  children,
  speed = 1,
  opacity = 1,
  shardCount = 18,
  interactive = true,
  variant = 'auto',
  ...props
}) {
  const isDark = useBackgroundTheme(variant);
  const bgClass = isDark
    ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950'
    : 'bg-gradient-to-br from-slate-50 via-white to-slate-50';

  const shards = React.useMemo(() => (
    Array.from({ length: shardCount }, (_, i) => {
      const depth = Math.random();
      return {
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 25 + depth * 80,
        shape: SHAPES[i % SHAPES.length],
        gradient: GRADIENTS[i % GRADIENTS.length],
        depth,
        blur: Math.max(0, (1 - depth) * 4),
        rotX: Math.random() * 360,
        rotY: Math.random() * 360,
        rotZ: Math.random() * 360,
        rotXDelta: (Math.random() - 0.5) * 60 * speed,
        rotYDelta: (Math.random() - 0.5) * 80 * speed,
        rotZDelta: (Math.random() - 0.5) * 40 * speed,
        floatDur: (8 + Math.random() * 12) / speed,
        floatDelay: -Math.random() * 15,
        floatAmp: 10 + depth * 30,
        baseOpacity: 0.2 + depth * 0.5,
      };
    })
  ), [shardCount, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      data-slot="crystal-shards-background"
      className={cn('relative size-full overflow-hidden', bgClass, className)}
      {...props}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ opacity }}>
        {shards.map((shard) => (
          <motion.div
            key={shard.id}
            className="absolute"
            style={{
              left: `${shard.x}%`,
              top: `${shard.y}%`,
              width: shard.size,
              height: shard.size,
              translateX: '-50%',
              translateY: '-50%',
              clipPath: shard.shape,
              background: shard.gradient,
              filter: `blur(${shard.blur}px)`,
              boxShadow: `0 0 ${shard.size * 0.5}px rgba(139,92,246,0.15)`,
              border: '1px solid rgba(255,255,255,0.05)',
            }}
            animate={{
              rotateX: [shard.rotX, shard.rotX + shard.rotXDelta, shard.rotX],
              rotateY: [shard.rotY, shard.rotY + shard.rotYDelta, shard.rotY],
              rotateZ: [shard.rotZ, shard.rotZ + shard.rotZDelta, shard.rotZ],
              y: [0, -shard.floatAmp, shard.floatAmp * 0.4, 0],
              x: [0, shard.floatAmp * 0.3, -shard.floatAmp * 0.2, 0],
              opacity: [shard.baseOpacity, shard.baseOpacity * 1.5, shard.baseOpacity * 0.8, shard.baseOpacity],
            }}
            transition={{
              duration: shard.floatDur,
              delay: shard.floatDelay,
              repeat: Infinity,
              ease: 'easeInOut',
              times: [0, 0.4, 0.7, 1],
            }}
          />
        ))}
      </div>

      {/* Subtle ambient light */}
      <div className="absolute inset-0 bg-radial from-white/[0.02] via-transparent to-transparent pointer-events-none" />

      {children && (
        <div className="relative z-10 size-full">{children}</div>
      )}
    </div>
  );
}

export { CrystalShardsBackground };
